import { useEffect, useRef, useState } from "react";
import { trackAcq, lastTool } from "@/lib/acquisition";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, googleLogin } from "@/pages/Login";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const trialPlan = params.get("plan");
  const signupViewed = useRef(false);
  const funnelProps = () => {
    const src = params.get("source");
    const tool = (src && src.startsWith("tool_")) ? src.slice(5) : lastTool();
    return { plan_id: trialPlan || undefined, tool, source_page: "register" };
  };

  useEffect(() => {
    if (signupViewed.current) return;
    signupViewed.current = true;
    trackAcq("signup_view", funnelProps());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError("Please accept the Terms of Service and Privacy Policy to continue."); return; }
    setError(""); setLoading(true);
    trackAcq("signup_submitted", funnelProps());
    let refCode = params.get("ref");
    try { refCode = refCode || localStorage.getItem("fv_ref"); } catch (e) { /* ignore */ }
    let acq = {};
    try {
      const src = params.get("source");
      const toolSlug = (src && src.startsWith("tool_")) ? src.slice(5) : (localStorage.getItem("fv_tool") || lastTool() || "");
      const { acqParams } = await import("@/lib/acquisition");
      acq = acqParams({ tool: toolSlug });
    } catch (e) { /* ignore */ }
    try {
      const { data } = await api.post("/auth/register", { name, email, password, trial_plan: trialPlan, ref_code: refCode || undefined, acq });
      try { if (refCode) localStorage.removeItem("fv_ref"); } catch (e) { /* ignore */ }
      try { localStorage.setItem("fv_onboard", "1"); } catch (e) { /* ignore */ }
      // Attribute signups that originated from a public SEO tool.
      try {
        let toolSlug = null;
        const src = params.get("source");
        if (src && src.startsWith("tool_")) toolSlug = src.slice(5);
        toolSlug = toolSlug || localStorage.getItem("fv_tool");
        if (toolSlug) {
          api.post("/public/track", { event: "tool_signup_created", tool: toolSlug }).catch(() => {});
          localStorage.removeItem("fv_tool");
        }
        // Store full acquisition attribution; flushed to /acquisition/link once the
        // session is active (covers hard-verification, login, and dev-bypass paths).
        const { acqParams } = await import("@/lib/acquisition");
        try { localStorage.setItem("fv_acq_pending", JSON.stringify(acqParams({ tool: toolSlug || lastTool() || "" }))); } catch (e) { /* ignore */ }
      } catch (e) { /* ignore */ }
      if (data?.requires_verification) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`, { state: { devVerifyUrl: data?.dev_verification_url } });
      } else {
        // DEV_AUTH_BYPASS: account auto-verified & logged in → straight to dashboard.
        setUser(data);
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const subtitle = trialPlan
    ? `Create your account to start your 7-day free ${trialPlan} trial — no credit card required.`
    : "Start building your business today.";

  let submitLabel = "Create account";
  if (loading) submitLabel = "Creating account…";
  else if (trialPlan) submitLabel = "Start my free trial";

  return (
    <AuthShell title="Create your account" subtitle={subtitle}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Full name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)}
            data-testid="register-name-input" placeholder="Jane Founder"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input" placeholder="you@company.com"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Password</Label>
          <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input" placeholder="At least 6 characters"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
        <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="register-terms-label">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
            data-testid="register-terms-checkbox"
            className="mt-0.5 w-4 h-4 shrink-0 accent-[#D4AF37] rounded" />
          <span className="text-xs text-[#A1A1AA] leading-relaxed">
            I agree to the{" "}
            <Link to="/legal/terms" target="_blank" data-testid="register-terms-link" className="text-[#D4AF37] hover:underline">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/legal/privacy" target="_blank" data-testid="register-privacy-link" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>.
          </span>
        </label>
        <Button type="submit" disabled={loading || !accepted} data-testid="register-submit-btn"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90 disabled:opacity-50">
          {submitLabel}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/8" /><span className="text-[#71717A] text-xs">OR</span><div className="flex-1 h-px bg-white/8" />
      </div>
      <Button onClick={googleLogin} data-testid="google-register-btn"
        className="w-full bg-white text-[#0A0A0B] hover:bg-white/90 h-11 rounded-lg font-medium border-0">
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.48 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
        Continue with Google
      </Button>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        Already have an account? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="goto-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
